import { api } from './api';

/**
 * Time Tracking Service
 * Handles all time tracking API calls
 */

/**
 * Start a timer for a task
 * @param {string} taskId - Task ID
 * @param {string} description - Optional description
 * @returns {Promise} API response
 */
export const startTimer = async (taskId, description = '') => {
  return api.post(`/tasks/${taskId}/time-entries/start`, {
    description
  });
};

/**
 * Stop a timer for a task
 * @param {string} taskId - Task ID
 * @returns {Promise} API response
 */
export const stopTimer = async (taskId) => {
  return api.post(`/tasks/${taskId}/time-entries/stop`);
};

/**
 * Get active timer for current user
 * @returns {Promise} API response
 */
export const getActiveTimer = async () => {
  return api.get('/tasks/active-timer');
};

/**
 * Stop all active timers for current user
 * Note: This is now handled automatically when starting a new timer
 * @returns {Promise} API response
 */
export const stopAllTimers = async () => {
  // This functionality is now automatic when starting a new timer
  // Just return a success response
  return { data: { success: true } };
};

/**
 * Get time summary for a task
 * @param {string} taskId - Task ID
 * @returns {Promise} API response
 */
export const getTaskTimeSummary = async (taskId) => {
  return api.get(`/tasks/${taskId}/time-summary`);
};

/**
 * Get all time entries for a task
 * @param {string} taskId - Task ID
 * @returns {Promise} API response
 */
export const getTaskTimeEntries = async (taskId) => {
  return api.get(`/tasks/${taskId}/time-entries`);
};

/**
 * Get current user's time entries
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date (ISO string)
 * @param {string} params.endDate - End date (ISO string)
 * @param {string} params.projectId - Project ID filter
 * @param {number} params.limit - Limit per page
 * @param {number} params.page - Page number
 * @returns {Promise} API response
 */
export const getMyTimeEntries = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      queryParams.append(key, params[key]);
    }
  });

  const queryString = queryParams.toString();
  return api.get(`/time-tracking/my-entries${queryString ? `?${queryString}` : ''}`);
};

/**
 * Create manual time entry
 * @param {Object} entryData - Time entry data
 * @param {string} entryData.taskId - Task ID
 * @param {string} entryData.startTime - Start time (ISO string)
 * @param {string} entryData.endTime - End time (ISO string)
 * @param {string} entryData.description - Optional description
 * @returns {Promise} API response
 */
export const createManualEntry = async (entryData) => {
  const { taskId, ...data } = entryData;
  return api.post(`/tasks/${taskId}/time-entries/manual`, data);
};

/**
 * Delete a time entry
 * @param {string} taskId - Task ID
 * @param {string} entryId - Time entry ID
 * @returns {Promise} API response
 */
export const deleteTimeEntry = async (taskId, entryId) => {
  return api.delete(`/tasks/${taskId}/time-entries/${entryId}`);
};

/**
 * Update time entry description
 * Note: This functionality can be added to the task API if needed
 * @param {string} taskId - Task ID
 * @param {string} entryId - Time entry ID
 * @param {string} description - New description
 * @returns {Promise} API response
 */
export const updateTimeEntry = async (taskId, entryId, description) => {
  // For now, time entries are immutable after creation
  // This can be implemented if needed
  return { data: { success: true } };
};

/**
 * Helper function to format duration from milliseconds
 * @param {number} duration - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "2h 30m")
 */
export const formatDuration = (duration) => {
  if (!duration || duration <= 0) return '0m';
  
  const totalMinutes = Math.floor(duration / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Helper function to calculate current duration for active timer
 * @param {Date} startTime - Timer start time
 * @returns {number} Current duration in milliseconds
 */
export const getCurrentDuration = (startTime) => {
  if (!startTime) return 0;
  
  const start = new Date(startTime);
  const now = new Date();
  return now - start;
};

/**
 * Helper function to format time for display
 * @param {Date} time - Time to format
 * @returns {string} Formatted time
 */
export const formatTime = (time) => {
  if (!time) return '';
  
  return new Date(time).toLocaleString();
};

/**
 * Helper function to validate manual time entry
 * @param {Object} entryData - Entry data to validate
 * @returns {Object} Validation result { isValid: boolean, errors: string[] }
 */
export const validateManualEntry = (entryData) => {
  const errors = [];
  
  if (!entryData.taskId) {
    errors.push('Task is required');
  }
  
  if (!entryData.startTime) {
    errors.push('Start time is required');
  }
  
  if (!entryData.endTime) {
    errors.push('End time is required');
  }
  
  if (entryData.startTime && entryData.endTime) {
    const startTime = new Date(entryData.startTime);
    const endTime = new Date(entryData.endTime);
    
    if (endTime <= startTime) {
      errors.push('End time must be after start time');
    }
    
    // Check if duration is reasonable (not more than 24 hours)
    const duration = endTime - startTime;
    const maxDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    if (duration > maxDuration) {
      errors.push('Duration cannot exceed 24 hours');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};